import React, { useState, useEffect, useCallback } from 'react';

const API = 'https://api.botanix.in/api';

const C = {
 bg:'#0A1628', surface:'#0F2040', surface2:'#0F2040',
 border:'rgba(30,58,95,.8)', border2:'rgba(30,58,95,1)',
 text:'#F0F2FF', text2:'#A0A8C0', text3:'#606880',
 accent:'#3AAF58', blue:'#FFFFFF',
 red:'#E05050', redBg:'rgba(224,80,80,.10)', redBorder:'rgba(224,80,80,.25)',
 amber:'#D4840F', amberBg:'rgba(212,132,15,.10)', amberBorder:'rgba(212,132,15,.25)',
 green:'#3AAF58', greenBg:'rgba(58,175,88,.10)', greenBorder:'rgba(58,175,88,.25)',
 purple:'#8060C0',
};

const inr = n => '₹' + Number(n||0).toLocaleString('en-IN',{maximumFractionDigits:0});
const fmtDate = d => d ? new Date(d).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'}) : '—';
const fmt = d => new Date(d).toISOString().slice(0,10);

const BOOKING_STATUS = {
 'Confirmed': { color:'#3AAF58', bg:'rgba(58,175,88,.2)' },
 'Tentative': { color:'#D4840F', bg:'rgba(212,132,15,.2)' },
 'Checked In': { color:'#2563A8', bg:'rgba(37,99,168,.2)' },
 'Checked Out': { color:'#8060C0', bg:'rgba(128,96,192,.2)' },
 'Cancelled': { color:'#606880', bg:'rgba(96,104,128,.15)' },
 'No Show': { color:'#E05050', bg:'rgba(224,80,80,.2)' },
};

const DAYS_SHORT = ['Su','Mo','Tu','We','Th','Fr','Sa'];

export default function SalesDashboard() {
 const [bookings, setBookings] = useState([]);
 const [loading, setLoading] = useState(true);
 const [tab, setTab] = useState('my_bookings');

 const currentUser = (() => { try { return JSON.parse(localStorage.getItem('botanix_user')); } catch { return null; } })();
 const myName = currentUser?.name || '';
 const roleKey = currentUser?.roleKey || '';
 const isSales = roleKey === 'SALES';

 const load = useCallback(async () => {
 setLoading(true);
 try {
 const r = await fetch(`${API}/bookings`);
 const d = await r.json();
 setBookings(Array.isArray(d) ? d : []);
 } catch {}
 finally { setLoading(false); }
 }, []);

 useEffect(() => { load(); }, [load]);

 // My bookings
 const myBookings = isSales ? bookings.filter(b => b.salesperson === myName) : bookings;

 // Stats
 const confirmed = myBookings.filter(b=>b.booking_status==='Confirmed').length;
 const tentative = myBookings.filter(b=>b.booking_status==='Tentative').length;
 const checkedIn = myBookings.filter(b=>b.booking_status==='Checked In').length;
 const cancelled = myBookings.filter(b=>b.booking_status==='Cancelled').length;
 const totalRev = myBookings.filter(b=>!['Cancelled','No Show'].includes(b.booking_status)).reduce((s,b)=>s+parseFloat(b.total_amount||0),0);
 const totalBal = myBookings.filter(b=>!['Cancelled','No Show'].includes(b.booking_status)).reduce((s,b)=>s+parseFloat(b.balance_amount||0),0);

 // Calendar
 const today = new Date();
 const [calStart, setCalStart] = useState(new Date(today.getFullYear(),today.getMonth(),1));
 const numDays = 30;
 const calDays = Array.from({length:numDays},(_,i)=>{ const d=new Date(calStart); d.setDate(calStart.getDate()+i); return d; });
 const todayStr = fmt(today);

 const getDateBookings = date => {
 const d = fmt(date);
 return myBookings.filter(b => b.start_date?.slice(0,10)<=d && b.end_date?.slice(0,10)>=d);
 };

 // Pipeline
 const pipeline = [
 { label:' Confirmed', count:confirmed, color:C.green, filter:'Confirmed' },
 { label:' Tentative', count:tentative, color:C.amber, filter:'Tentative' },
 { label:' Checked In', count:checkedIn, color:C.blue, filter:'Checked In' },
 { label:' Cancelled', count:cancelled, color:C.red, filter:'Cancelled' },
 ];

 const [activeFilter, setActiveFilter] = useState('All');
 const displayBookings = activeFilter==='All' ? myBookings : myBookings.filter(b=>b.booking_status===activeFilter);

 return (
 <div style={{ padding:24, background:C.bg, minHeight:'100vh', color:C.text, fontFamily:'Inter,sans-serif' }}>

 {/* Header */}
 <div style={{ marginBottom:24 }}>
 <div style={{ fontSize:22, fontWeight:800 }}> {isSales?'My Sales Dashboard':'Sales Overview'}</div>
 <div style={{ fontSize:13, color:C.text2, marginTop:2 }}>
 {isSales ? `Welcome, ${myName} · ${myBookings.length} bookings` : `All sales · ${bookings.length} total bookings`}
 </div>
 </div>

 {/* KPI Cards */}
 <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))', gap:14, marginBottom:24 }}>
 {[
 {label:'Total Bookings', value:myBookings.length, color:C.text, },
 {label:'Confirmed', value:confirmed, color:C.green, },
 {label:'Tentative', value:tentative, color:C.amber, },
 {label:'Checked In', value:checkedIn, color:C.blue, },
 {label:'Total Revenue', value:inr(totalRev), color:C.accent, icon:'₹'},
 {label:'Outstanding', value:inr(totalBal), color:C.amber, },
 ].map(s=>(
 <div key={s.label} style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:12, padding:'16px' }}>
 <div style={{ fontSize:20, marginBottom:4 }}>{s.icon}</div>
 <div style={{ fontSize:20, fontWeight:900, color:s.color }}>{s.value}</div>
 <div style={{ fontSize:11, color:C.text3, marginTop:2 }}>{s.label}</div>
 </div>
 ))}
 </div>

 {/* Tabs */}
 <div style={{ display:'flex', gap:4, marginBottom:20, background:C.surface, borderRadius:10, padding:4, width:'fit-content' }}>
 {[
 {id:'my_bookings', label:' My Bookings'},
 {id:'calendar', label:' Booking Calendar'},
 {id:'pipeline', label:' Pipeline'},
 ].map(t=>(
 <button key={t.id} onClick={()=>setTab(t.id)}
 style={{ padding:'8px 18px', borderRadius:8, border:'none', cursor:'pointer', fontSize:13, fontWeight:600, background:tab===t.id?C.accent:'transparent', color:tab===t.id?'#fff':C.text2, whiteSpace:'nowrap' }}>
 {t.label}
 </button>
 ))}
 </div>

 {loading && <div style={{ textAlign:'center', padding:40, color:C.text2 }}>Loading…</div>}

 {/* ── MY BOOKINGS ── */}
 {!loading && tab==='my_bookings' && (
 <div>
 {/* Status filter pills */}
 <div style={{ display:'flex', gap:8, marginBottom:16, flexWrap:'wrap' }}>
 {['All','Confirmed','Tentative','Checked In','Checked Out','Cancelled'].map(s => {
 const bst = BOOKING_STATUS[s]||{color:C.text2,bg:C.surface2};
 const cnt = s==='All' ? myBookings.length : myBookings.filter(b=>b.booking_status===s).length;
 return (
 <button key={s} onClick={()=>setActiveFilter(s)}
 style={{ padding:'6px 14px', borderRadius:99, border:`2px solid ${activeFilter===s?bst.color:'transparent'}`, background:activeFilter===s?bst.bg:C.surface2, color:activeFilter===s?bst.color:C.text2, cursor:'pointer', fontSize:12, fontWeight:600 }}>
 {s} ({cnt})
 </button>
 );
 })}
 </div>

 <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:14, overflow:'hidden' }}>
 {displayBookings.length===0
 ? <div style={{ textAlign:'center', padding:40, color:C.text2 }}>No bookings found</div>
 : (
 <div style={{ overflowX:'auto' }}>
 <table style={{ width:'100%', borderCollapse:'collapse' }}>
 <thead>
 <tr style={{ borderBottom:`1px solid ${C.border}` }}>
 {['Code','Client','Type','Event Date','Pax','Amount','Balance','Status','Source'].map(h=>(
 <th key={h} style={{ padding:'10px 14px', fontSize:11, color:C.text2, fontWeight:600, textAlign:'left', textTransform:'uppercase', letterSpacing:'.05em', whiteSpace:'nowrap' }}>{h}</th>
 ))}
 </tr>
 </thead>
 <tbody>
 {displayBookings.sort((a,b)=>new Date(b.created_at)-new Date(a.created_at)).map((b,i)=>{
 const bst = BOOKING_STATUS[b.booking_status]||BOOKING_STATUS['Confirmed'];
 return (
 <tr key={b.id} style={{ borderBottom:'1px solid rgba(255,255,255,.04)', background:i%2===0?'transparent':'rgba(255,255,255,.02)' }}>
 <td style={{ padding:'10px 14px', fontSize:13, fontWeight:700, color:C.accent, whiteSpace:'nowrap' }}>{b.booking_code}</td>
 <td style={{ padding:'10px 14px', fontSize:13 }}>
 <div style={{ fontWeight:600, color:C.text }}>{b.guest_name||b.company_name||'—'}</div>
 <div style={{ fontSize:11, color:C.text3 }}>{b.guest_phone||''}</div>
 </td>
 <td style={{ padding:'10px 14px', fontSize:12, color:C.text2, whiteSpace:'nowrap' }}>{b.booking_type}</td>
 <td style={{ padding:'10px 14px', fontSize:12, color:C.text2, whiteSpace:'nowrap' }}>
 <div>{fmtDate(b.start_date)}</div>
 {b.end_date!==b.start_date && <div style={{ fontSize:11, color:C.text3 }}>to {fmtDate(b.end_date)}</div>}
 </td>
 <td style={{ padding:'10px 14px', fontSize:13, color:C.text, textAlign:'center' }}>{b.pax_count}</td>
 <td style={{ padding:'10px 14px', fontSize:13, fontWeight:700, color:C.accent, whiteSpace:'nowrap' }}>{inr(b.total_amount)}</td>
 <td style={{ padding:'10px 14px', fontSize:13, color:parseFloat(b.balance_amount)>0?C.amber:C.text3, whiteSpace:'nowrap' }}>{inr(b.balance_amount)}</td>
 <td style={{ padding:'10px 14px', whiteSpace:'nowrap' }}>
 <span style={{ padding:'3px 10px', borderRadius:99, fontSize:11, fontWeight:700, background:bst.bg, color:bst.color }}>{b.booking_status}</span>
 </td>
 <td style={{ padding:'10px 14px', fontSize:12, color:C.text2 }}>{b.source||'—'}</td>
 </tr>
 );
 })}
 </tbody>
 </table>
 </div>
 )}
 </div>
 </div>
 )}

 {/* ── CALENDAR ── */}
 {!loading && tab==='calendar' && (
 <div>
 <div style={{ display:'flex', gap:14, marginBottom:14, flexWrap:'wrap' }}>
 {Object.entries(BOOKING_STATUS).map(([s,st])=>(
 <div key={s} style={{ display:'flex', alignItems:'center', gap:5, fontSize:11, color:C.text2 }}>
 <div style={{ width:12, height:12, borderRadius:3, background:st.color }} />{s}
 </div>
 ))}
 </div>

 <div style={{ display:'flex', gap:8, alignItems:'center', marginBottom:16 }}>
 <button onClick={()=>{const d=new Date(calStart);d.setMonth(d.getMonth()-1);setCalStart(new Date(d.getFullYear(),d.getMonth(),1));}} style={{ padding:'6px 14px', background:C.surface, border:`1px solid ${C.border2}`, borderRadius:8, color:C.text2, cursor:'pointer' }}></button>
 <div style={{ fontWeight:700, fontSize:14, color:C.text, minWidth:160, textAlign:'center' }}>
 {calStart.toLocaleDateString('en-IN',{month:'long',year:'numeric'})}
 </div>
 <button onClick={()=>{const d=new Date(calStart);d.setMonth(d.getMonth()+1);setCalStart(new Date(d.getFullYear(),d.getMonth(),1));}} style={{ padding:'6px 14px', background:C.surface, border:`1px solid ${C.border2}`, borderRadius:8, color:C.text2, cursor:'pointer' }}></button>
 <button onClick={()=>setCalStart(new Date(today.getFullYear(),today.getMonth(),1))} style={{ padding:'6px 14px', background:C.surface, border:`1px solid ${C.border2}`, borderRadius:8, color:C.accent, cursor:'pointer', fontWeight:600 }}>Today</button>
 </div>

 {/* Month grid */}
 <div style={{ background:C.surface, borderRadius:14, overflow:'hidden', border:`1px solid ${C.border}` }}>
 <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', borderBottom:`1px solid ${C.border}` }}>
 {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d=>(
 <div key={d} style={{ padding:'10px', textAlign:'center', fontSize:11, fontWeight:700, color:C.text3, textTransform:'uppercase' }}>{d}</div>
 ))}
 </div>
 <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)' }}>
 {/* Empty cells */}
 {Array.from({length:calStart.getDay()},(_,i)=>(
 <div key={`e${i}`} style={{ minHeight:90, borderRight:`1px solid ${C.border}`, borderBottom:`1px solid ${C.border}`, background:'rgba(255,255,255,.01)' }} />
 ))}
 {/* Days */}
 {Array.from({length:new Date(calStart.getFullYear(),calStart.getMonth()+1,0).getDate()},(_,i)=>{
 const date = new Date(calStart.getFullYear(),calStart.getMonth(),i+1);
 const dayBks = getDateBookings(date);
 const isToday = fmt(date)===todayStr;
 const isPast = date < new Date(today.getFullYear(),today.getMonth(),today.getDate());
 return (
 <div key={i} style={{ minHeight:90, padding:6, borderRight:`1px solid ${C.border}`, borderBottom:`1px solid ${C.border}`, background:isToday?`${C.accent}10`:isPast?'rgba(255,255,255,.01)':'transparent' }}>
 <div style={{ fontSize:13, fontWeight:isToday?900:600, color:isToday?C.accent:isPast?C.text3:C.text, marginBottom:4 }}>{i+1}</div>
 {dayBks.slice(0,3).map((b,bi)=>{
 const bst = BOOKING_STATUS[b.booking_status]||BOOKING_STATUS['Confirmed'];
 return (
 <div key={bi} title={`${b.guest_name||'Guest'} · ${b.booking_status} · ${b.salesperson||''}`}
 style={{ padding:'2px 5px', background:bst.bg, borderLeft:`3px solid ${bst.color}`, borderRadius:'0 4px 4px 0', marginBottom:2, fontSize:9, fontWeight:700, color:bst.color, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
 {b.guest_name||b.company_name||'Guest'}
 </div>
 );
 })}
 {dayBks.length>3 && <div style={{ fontSize:9, color:C.text3 }}>+{dayBks.length-3} more</div>}
 </div>
 );
 })}
 </div>
 </div>
 </div>
 )}

 {/* ── PIPELINE ── */}
 {!loading && tab==='pipeline' && (
 <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))', gap:16 }}>
 {pipeline.map(p => {
 const pipeBookings = myBookings.filter(b=>b.booking_status===p.filter);
 const pipeRev = pipeBookings.reduce((s,b)=>s+parseFloat(b.total_amount||0),0);
 return (
 <div key={p.filter} style={{ background:C.surface, border:`1px solid ${p.color}44`, borderRadius:14, overflow:'hidden' }}>
 <div style={{ padding:'14px 16px', background:`${p.color}15`, borderBottom:`1px solid ${p.color}33` }}>
 <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
 <div style={{ fontWeight:700, fontSize:14, color:p.color }}>{p.label}</div>
 <div style={{ fontWeight:900, fontSize:22, color:p.color }}>{p.count}</div>
 </div>
 <div style={{ fontSize:12, color:C.text2, marginTop:4 }}>Revenue: {inr(pipeRev)}</div>
 </div>
 <div style={{ padding:12, maxHeight:300, overflowY:'auto' }}>
 {pipeBookings.length===0 && <div style={{ fontSize:12, color:C.text3, textAlign:'center', padding:20 }}>No bookings</div>}
 {pipeBookings.map(b=>(
 <div key={b.id} style={{ padding:'8px 10px', background:C.surface2, borderRadius:8, marginBottom:8 }}>
 <div style={{ fontWeight:600, fontSize:12, color:C.text }}>{b.guest_name||b.company_name||'—'}</div>
 <div style={{ fontSize:11, color:C.text3, marginTop:2 }}>{b.booking_type} · {b.pax_count} pax</div>
 <div style={{ display:'flex', justifyContent:'space-between', marginTop:4 }}>
 <div style={{ fontSize:11, color:C.text3 }}>{fmtDate(b.start_date)}</div>
 <div style={{ fontSize:12, fontWeight:700, color:C.accent }}>{inr(b.total_amount)}</div>
 </div>
 </div>
 ))}
 </div>
 </div>
 );
 })}
 </div>
 )}
 </div>
 );
}
